import React, { useState } from 'react';

import {
  Tr,
  Td,
  HStack,
  IconButton,
  Icon,
  Alert,
  AlertIcon,
  AlertDescription,
  useDisclosure,
  useColorModeValue,
  Tooltip,
  Box,
} from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { BiChevronUp, BiChevronDown } from 'react-icons/bi';
import { FiLock } from 'react-icons/fi';
import { useParams } from 'react-router';

import CopyToClipboardButton from 'components/shared/CopyToClipboardButton';
import { LciaMethod, LciaScore } from 'hooks/data/datasets/lcia/types';
import { RecentMethod } from 'hooks/useSavedLciaMethod/types';
import { useDatasetSession } from 'pages/Dataset/DatasetSessionContext';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';
import { formatNumericalValue } from 'utilities/numbers';

import DirectContributionView from '../DirectContribution';

type Props = {
  score: LciaScore;
  selectedMethod?: RecentMethod;
  hideScores?: boolean;
};

const ImpactAssessmentRow = ({ score, selectedMethod, hideScores }: Props) => {
  const { spold_id } = useParams();
  const { increment } = useDatasetSession();

  const { isOpen, onToggle } = useDisclosure();

  const bgColor = useColorModeValue('gray.100', 'blackAlpha.200');

  const [hovering, setHovering] = useState(false);
  return (
    <React.Fragment>
      <Tr
        bg={isOpen || hovering ? 'blackAlpha.50' : 'auto'}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        onClick={
          hideScores
            ? undefined
            : () => {
                if (!isOpen) {
                  mixpanel.track(MIXPANEL_EVENTS.IMPACT_ASSESSMENT_CATEGORY_EXPANDED, {
                    spold_id,
                    category: score.category_name,
                    indicator: score.indicator_name,
                  });
                  increment('impact_assessment_action_count');
                }
                onToggle();
              }
        }
        cursor={hideScores ? 'default' : 'pointer'}
      >
        <Td pl={4}>{score.category_name}</Td>
        <Td>{score.indicator_name}</Td>
        <Td isNumeric>
          {hideScores ? (
            <Tooltip hasArrow label="Sign in or upgrade to see impact scores" placement="top">
              <Box as="span" display="inline-block">
                <Icon as={FiLock} color="gray.400" />
              </Box>
            </Tooltip>
          ) : (
            formatNumericalValue(score.amount)
          )}
        </Td>
        <Td whiteSpace={'nowrap'}>{score.unit_name}</Td>
        <Td px={0}>
          {!hideScores && (
            <HStack>
              <Tooltip hasArrow label="Copy value" placement="top">
                <Box display="inline-block" tabIndex={0}>
                  <CopyToClipboardButton
                    value={score.amount}
                    opacity={hovering ? 1 : 0}
                    onValueCopied={() => {
                      mixpanel.track(MIXPANEL_EVENTS.COPY_DATASET_VALUE, {
                        spold_id,
                        type: 'impact_score',
                        category: score.category_name,
                        indicator: score.indicator_name,
                        lcia_method: selectedMethod?.method_name,
                      });
                      increment('copy_dataset_value_count');
                      increment('impact_assessment_action_count');
                    }}
                  />
                </Box>
              </Tooltip>
              <IconButton
                aria-label="expand score"
                variant={'ghost'}
                icon={<Icon as={isOpen ? BiChevronUp : BiChevronDown} />}
              />
            </HStack>
          )}
        </Td>
      </Tr>
      {isOpen && !hideScores && (
        <Tr>
          <Td colSpan={5} px={5} pb={5} pt={5} bg={bgColor} borderBottomRadius="md">
            {score.amount === 0 ? (
              <Alert variant="info">
                <AlertIcon />
                <AlertDescription>No contributors, the total score for this indicator is 0.</AlertDescription>
              </Alert>
            ) : (
              <DirectContributionView
                category={score.category_name}
                indicatorName={score.indicator_name}
                spoldId={Number(spold_id)}
                indicatorId={score.index}
                unitName={score.unit_name}
                method={selectedMethod as LciaMethod}
              />
            )}
          </Td>
        </Tr>
      )}
    </React.Fragment>
  );
};

export default ImpactAssessmentRow;
